import { ArrowDownward, ArrowUpward, Clear, Close, ExpandMore } from "@mui/icons-material";
import {
    Box,
    Button,
    Divider,
    FormControl,
    IconButton,
    ListItemText,
    MenuItem,
    MenuList,
    Select,
    TextField,
    Typography,
} from "@mui/material";

/**
 * Renders the active filter popover content.
 *
 * This is intentionally extracted to keep `dataTables.js` as a thin container.
 * It is still UI-heavy, but now it is composable and can be replaced per-table if needed.
 */
export function renderActiveFilterPopover({
  activeFilterField,
  activeFilterColumn,
  filterPopoverByField,
  filterPopoverContext,
  isTextFilterPopover,
  shouldShowQuickSortMenu,
  filters,
  setFilters,
  sortConfig,
  handleSort,
  handleFilterChange,
  handleFilterMenuClose,
}) {
  if (!activeFilterField) return null;

  const mapped = filterPopoverByField?.[activeFilterField];
  const mappedNode = typeof mapped === "function" ? mapped(filterPopoverContext) : null;
  if (mappedNode) {
    return mappedNode;
  }

  if (activeFilterColumn?.type === "sort") {
    return (
      <Box sx={{ minWidth: 200, width: 250, p: 0, m: 0, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            pt: 0.5,
            pb: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold", color: "#111827" }}>
            Filter {activeFilterColumn?.headerName || "Column"}
          </Typography>
          <IconButton size="small" onClick={handleFilterMenuClose} sx={{ p: 0.5, color: "#6B7280" }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ width: "100%", m: 0, p: 0 }} />

        <Typography
          variant="body2"
          sx={{ px: 1.5, pt: 1.5, pb: 1, color: "#627CFB", fontWeight: 500, fontSize: "0.875rem" }}
        >
          Sort by
        </Typography>

        <FormControl fullWidth variant="standard" sx={{ px: 1.5, mb: 1.5 }}>
          <Select
            value={sortConfig.field === activeFilterField ? sortConfig.direction : ""}
            onChange={(e) => {
              const direction = e.target.value;
              if (direction) {
                handleSort(activeFilterField, direction);
              } else {
                handleSort(null, null);
              }
              handleFilterMenuClose();
            }}
            displayEmpty
            IconComponent={ExpandMore}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#000000" }}>
                    Select Sort Option
                  </Typography>
                );
              }
              return (
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  {selected === "asc" ? "Ascending (A-Z)" : selected === "desc" ? "Descending (Z-A)" : ""}
                </Typography>
              );
            }}
            sx={{
              fontSize: "0.875rem",
              width: "100%",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                paddingBottom: "8px",
                fontSize: "0.875rem",
              },
              "&:before": {
                borderBottom: "2px solid #3B82F6",
              },
              "&:after": {
                borderBottom: "2px solid #3B82F6",
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottom: "2px solid #3B82F6",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                  zIndex: 99999,
                  width: "auto",
                },
              },
              style: {
                zIndex: 99999,
              },
            }}
          >
            <MenuItem value="asc" sx={{ py: 1, px: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                Ascending (A-Z)
              </Typography>
            </MenuItem>
            <MenuItem value="desc" sx={{ py: 1, px: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                Descending (Z-A)
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        {sortConfig.field === activeFilterField && (
          <Button
            onClick={() => {
              handleSort(null, null);
              handleFilterMenuClose();
            }}
            size="small"
            fullWidth
            sx={{
              mb: 0,
              mx: 1.5,
              width: "calc(100% - 24px)",
              color: "#627CFB",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              "&:hover": {
                backgroundColor: "rgba(98, 124, 251, 0.04)",
              },
            }}
          >
            Clear Sort
          </Button>
        )}
      </Box>
    );
  }

  if (!isTextFilterPopover && !shouldShowQuickSortMenu) return null;

  return (
    <Box sx={{ p: 2, minWidth: 200 }}>
      {isTextFilterPopover ? (
        <Box>
          <TextField
            size="small"
            placeholder={activeFilterField ? `Filter ${activeFilterColumn?.headerName || ""}` : "Filter"}
            value={filters[activeFilterField] || ""}
            onChange={(e) => {
              setFilters((prev) => ({
                ...prev,
                [activeFilterField]: e.target.value,
              }));
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleFilterChange(activeFilterField, filters[activeFilterField], true);
                handleFilterMenuClose();
              }
            }}
            fullWidth
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused": {
                  "& fieldset": {
                    borderColor: "#627CFB",
                    borderWidth: "2px",
                  },
                },
                "& fieldset": {
                  borderColor: "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: "#627CFB",
                },
              },
            }}
          />
          <Button
            onClick={() => {
              handleFilterChange(activeFilterField, filters[activeFilterField], true);
              handleFilterMenuClose();
            }}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#627CFB",
              color: "white",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              mb: 1,
              "&:hover": {
                backgroundColor: "#536DEC",
              },
            }}
          >
            Search
          </Button>
          {filters[activeFilterField] && (
            <Button
              onClick={() => {
                handleFilterChange(activeFilterField, "", true);
                handleFilterMenuClose();
              }}
              size="small"
              fullWidth
              sx={{
                color: "#627CFB",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
                "&:hover": {
                  backgroundColor: "rgba(98, 124, 251, 0.04)",
                },
              }}
            >
              Clear Filter
            </Button>
          )}
        </Box>
      ) : null}

      {shouldShowQuickSortMenu && (
        <MenuList>
          <MenuItem
            onClick={() => {
              handleSort(activeFilterField, "asc");
              handleFilterMenuClose();
            }}
            sx={{
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            <ArrowUpward fontSize="small" sx={{ mr: 1 }} />
            <ListItemText
              primary={activeFilterField === "latestLogin" ? "Newest to Oldest" : "Sort Ascending (A-Z)"}
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleSort(activeFilterField, "desc");
              handleFilterMenuClose();
            }}
            sx={{
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            <ArrowDownward fontSize="small" sx={{ mr: 1 }} />
            <ListItemText
              primary={activeFilterField === "latestLogin" ? "Oldest to Newest" : "Sort Descending (Z-A)"}
            />
          </MenuItem>

          {sortConfig.field === activeFilterField && sortConfig.direction && (
            <MenuItem
              onClick={() => {
                handleSort(null, null);
                handleFilterMenuClose();
              }}
              sx={{
                px: 2,
                py: 1,
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              <Clear fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary="Clear Sort" />
            </MenuItem>
          )}

          {filters[activeFilterField] && (
            <MenuItem
              onClick={() => {
                handleFilterChange(activeFilterField, "", true);
                handleFilterMenuClose();
              }}
              sx={{
                px: 2,
                py: 1,
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              <Clear fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary="Clear Filter" />
            </MenuItem>
          )}
        </MenuList>
      )}
    </Box>
  );
}
